import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import WebService from '../../Services/WebService';
import { TrashFill } from 'react-bootstrap-icons';
import { MenuAttributesUpdated } from '../../components/Common/BackendAttributes/BackendAttributes';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import './OrderView.scss';
import AddItemModal from './AddItemModal';
import MobileLoader from '../../components/MobileLoader/MobileLoader';
import { getEncryptedSessionData, setEncryptedSessionData } from '../../Services/SessionStorageService';
import QrOrderRealtimeService, { QrOrderPayload } from '../../Services/QrOrderRealtimeService';
import { getPosOrderCacheKey } from '../TableView/TableView';
import { io } from 'socket.io-client';

const getPosSocket = () => {
    const envUrl = process.env.REACT_APP_POS_SOCKET_URL;
    const stored = localStorage.getItem("pos_socket_url");
    const host = window.location.hostname;
    let url = envUrl || stored || "http://localhost:3007";
    if (!envUrl && (!stored || stored === "http://localhost:3007") && host !== "localhost" && host !== "127.0.0.1") {
        url = `http://${host}:3007`;
    }
    return io(url, {
        autoConnect: true,
        transports: ["websocket", "polling"],
        reconnectionDelay: 5000,
        timeout: 5000
    });
};
let _posSocket: ReturnType<typeof io> | null = null;
const posSocket = () => {
    if (!_posSocket) _posSocket = getPosSocket();
    return _posSocket;
};

export interface paymentTypeInterface {
    payment_type: 'CASH' | 'CARD' | 'UPI' | 'COMPLIMENTARY' | "REELO" | "DUE" | "PART";
    amount: number;
    isChecked: boolean;
    disabled?: boolean;
    is_radio?: boolean;
    is_checkbox?: boolean;
}

const OrderView = () => {
    const [selectedMenuItem, setSelectedMenuItem] = useState<any[]>([]);
    const [totalQuantity, setTotalQuantity] = useState<number>(0);
    const [totalAmount, setTotalAmount] = useState<number>(0);
    const [showFullPageLoader, setShowFullPageLoader] = useState<boolean>(false);
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const loungeId = searchParams.get("loungeId");
    const qrOrderParam = searchParams.get("qrOrder");
    const orderIdParam = searchParams.get("orderId");
    const tokenParam = searchParams.get("token_number");
    const [tokenNumber, setTokenNumber] = useState<number | null>(null);
    const [showModal, setShowModal] = useState(false);
    const tenantId = localStorage.getItem("tenantId") || "default";
    const qrOrderCacheKey = `qr_table_orders_${tenantId}`;
    const [qrOrder, setQrOrder] = useState<QrOrderPayload | null>(null);
    const [originalMenus, setOriginalMenus] = useState<any[]>([]);

    useEffect(() => {
        if (!tokenParam || tokenParam === "undefined") return;
        setTokenNumber(parseInt(tokenParam));
    }, [tokenParam]);

    // ─── formatMenus — index fallback taaki koi bhi item skip na ho ──────────

    const formatMenus = (menus: any[]) => {
        const mergedList: any[] = [];
        menus.forEach((item: any, idx: number) => {
            const menuId = String(item.menu_id || item.id || item.item_id || `item_${idx}`);
            const existing = mergedList.find((m) => String(m.menu_id || m.id) === menuId);
            if (existing) {
                existing.quantity = Number(existing.quantity || 1) + Number(item.quantity || 1);
            } else {
                mergedList.push({
                    ...item,
                    menu_id: menuId,
                    id: menuId,
                    name: item.name || item.item_name || item.menu_name || "",
                    gross_price: Number(item.gross_price ?? item.price ?? item.unit_price ?? 0),
                    quantity: Number(item.quantity || 1),
                    total_price: Number(item.total_price || 0),
                    kot_id: item.kot_id || null,
                });
            }
        });
        return mergedList;
    };

    const updateCurrentPosOrderCache = useCallback((tableNo: string, menus: any[], order?: QrOrderPayload) => {
        const posKey = getPosOrderCacheKey(tenantId);
        const existing = getEncryptedSessionData<Record<string, any>>(posKey) || {};

        if (menus.length === 0) {
            delete existing[tableNo];
        } else {
            existing[tableNo] = {
                ...(existing[tableNo] || {}),
                menus,
                timestamp: order?.timestamp || new Date().toISOString(),
                type: "POS_ORDER",
                loungeId: order?.loungeId || order?.lounge_id || loungeId,
                tokenNumber: order?.tokenNumber || order?.token_number || tokenNumber || "",
                isNew: false,
            };
        }

        setEncryptedSessionData(posKey, existing);
    }, [loungeId, tenantId, tokenNumber]);


    useEffect(() => {
        if (qrOrderParam !== "1" || !id) return;
        const stateOrder = (location.state as any)?.qrOrder as QrOrderPayload | undefined;

        const posKey = getPosOrderCacheKey(tenantId);
        const posOrders = getEncryptedSessionData<Record<string, any>>(posKey) || {};
        const posOrderData = posOrders[id];
        const posOrder: QrOrderPayload | null = posOrderData ? {
            tableNo: id,
            table_no: id,
            menus: posOrderData.menus || [],
            type: posOrderData.type || "POS_ORDER",
            loungeId: posOrderData.loungeId,
            lounge_id: posOrderData.loungeId,
            tokenNumber: posOrderData.tokenNumber,
            token_number: posOrderData.tokenNumber,
            timestamp: posOrderData.timestamp,
        } : null;

        const cachedOrders = getEncryptedSessionData<Record<string, QrOrderPayload>>(qrOrderCacheKey) || {};
        const cachedOrder = cachedOrders[id];

        // Jis source mein menus hon use priority do
        const order = (stateOrder && stateOrder.menus && stateOrder.menus.length > 0) ? stateOrder : (posOrder && posOrder.menus && posOrder.menus.length > 0) ? posOrder : cachedOrder || null;
        if (!order || !order.menus || order.menus.length === 0) return;
        if (orderIdParam && order.orderId && String(order.orderId) !== String(orderIdParam)) return;

        setQrOrder(order);
        const formatted = formatMenus(order.menus);
        setSelectedMenuItem(formatted);
        setOriginalMenus(formatted.map((m: any) => ({ ...m })));

    }, [id, location.state, orderIdParam, qrOrderCacheKey, qrOrderParam, tenantId]);

    useEffect(() => {
        if (!id) return;

        const unsubscribe = QrOrderRealtimeService.subscribe((order: QrOrderPayload) => {
            // ── Tenant ID check ──
            const orderTenantId = String(order.tenantId || order.tenant_id || "");
            const localTenantId = localStorage.getItem("tenantId") || "default";
            if (orderTenantId && orderTenantId !== localTenantId) {
                console.log("Tenant ID mismatch, ignoring order event:", orderTenantId, "expected:", localTenantId);
                return;
            }

            const tableNo = String(order.tableNo || order.table_no || "").trim();
            if (!tableNo || tableNo !== String(id).trim()) return;

            if (order.action === "CLEAR_TABLE") {
                setQrOrder(null);
                setSelectedMenuItem([]);
                setOriginalMenus([]);
                updateCurrentPosOrderCache(tableNo, [], order);
                const cachedOrders = getEncryptedSessionData<Record<string, QrOrderPayload>>(qrOrderCacheKey) || {};
                delete cachedOrders[tableNo];
                setEncryptedSessionData(qrOrderCacheKey, cachedOrders);
                return;
            }

            if (order.action === "FULL_REPLACE" && Array.isArray(order.menus)) {
                const formatted = formatMenus(order.menus);
                setQrOrder({ ...order, menus: formatted });
                setSelectedMenuItem(formatted);
                setOriginalMenus(formatted.map((m: any) => ({ ...m })));
                updateCurrentPosOrderCache(tableNo, formatted, order);
                return;
            }

            if (order.action === "DELETE_ITEM" && (order.menu_id || order.menuId)) {
                const removedMenuId = String(order.menu_id || order.menuId);
                setSelectedMenuItem((prev: any[]) => {
                    const next = prev.filter((item: any) => String(item.menu_id || item.id) !== removedMenuId);
                    updateCurrentPosOrderCache(tableNo, next, order);
                    return next;
                });
                setOriginalMenus((prev: any[]) => prev.filter((item: any) => String(item.menu_id || item.id) !== removedMenuId));
            }
        });

        return unsubscribe;
    }, [id, updateCurrentPosOrderCache, qrOrderCacheKey]);

    useEffect(() => {
        const qty = selectedMenuItem.reduce((sum, item: any) => sum + Number(item.quantity || 0), 0);
        const amount = selectedMenuItem.reduce((sum, item: any) => sum + Number(item.quantity || 0) * Number(item.gross_price || 0), 0);
        setTotalQuantity(qty);
        setTotalAmount(amount);
    }, [selectedMenuItem]);

    const handleModalClose = (status?: boolean, data?: any) => {
        setShowModal(false);
        if (status && data?.length > 0) {
            const formattedData = data.map((item: any) => ({
                menu_id: item.id, id: item.id, name: item.name,
                gross_price: Number(item.gross_price || 0),
                quantity: Number(item.quantity || 1), kot_id: null,
            }));
            setSelectedMenuItem((prev: any) => {
                const merged = [...prev];
                formattedData.forEach((newItem: any) => {
                    const existingIndex = merged.findIndex((i: any) => String(i.menu_id) === String(newItem.menu_id));
                    if (existingIndex > -1) merged[existingIndex].quantity = Number(merged[existingIndex].quantity || 1) + 1;
                    else merged.push(newItem);
                });
                return merged;
            });
        }
    };

    const onRemoveMenuItem = ({ index, menu_id }: { index: number; menu_id: number; kot_id?: any; token_number?: any }) => {
        setSelectedMenuItem((prev: any[]) => prev.filter((item: any, i: number) => !(i === index && String(item.id) === String(menu_id))));
    };

    const onAddItemQuantity = (menu_id: number) => {
        setSelectedMenuItem((prev: any[]) => prev.map((item: any) => String(item.id) === String(menu_id) ? { ...item, quantity: Number(item.quantity || 1) + 1 } : item));
    };

    const onRemoveItemQuantity = (menu_id: number) => {
        setSelectedMenuItem((prev: any[]) => prev.map((item: any) => String(item.id) === String(menu_id) ? { ...item, quantity: Math.max(1, Number(item.quantity || 1) - 1) } : item));
    };


    const onSaveAndPrintOrder = () => {
        if (!selectedMenuItem || selectedMenuItem.length === 0) {
            toast.error("Please add atleast one menu item");
            return;
        }
        setLoading(true);
        const menusPayload = selectedMenuItem.map((item: any) => {
            const menuId = String(item.menu_id || item.id || "");
            const currentQty = Number(item.quantity || 1);
            const original = originalMenus.find((o: any) => String(o.menu_id || o.id || "") === menuId);
            const originalQty = original ? Number(original.quantity || 1) : 0;
            const deltaQty = currentQty - originalQty;
            if (deltaQty <= 0 && originalQty > 0) return null;
            return {
                id: item.menu_id || item.id,
                quantity: originalQty === 0 ? currentQty : deltaQty,
                gross_price: Number(item.gross_price || 0),
            };
        }).filter(Boolean);

        if (menusPayload.length === 0) {
            toast.info("No changes to save");
            setLoading(false);
            return;
        }

        const userInfoStr = localStorage.getItem("user_info");
        const userInfo = userInfoStr ? JSON.parse(userInfoStr) : null;

        WebService.postAPI({
            action: `/create/waiter-order`,
            body: { table_no: id || "", tenantId: tenantId || "", menus: menusPayload, waiter_id: userInfo?.id || "" },
            id: "saveUserButton",
        })
            .then((res: any) => {
                toast.success("Order Created Successfully");
                setLoading(false);
                // Yeh FULL_REPLACE event aane tak ya manually clear hone tak rahega
                const savedMenus = selectedMenuItem.map((item: any, idx: number) => ({
                    ...item,
                    menu_id: item.menu_id || item.id || `item_${idx}`,
                    id: item.id || item.menu_id || `item_${idx}`,
                    name: item.name || "",
                    gross_price: Number(item.gross_price || 0),
                    quantity: Number(item.quantity || 1),
                }));
                const posKey = getPosOrderCacheKey(tenantId);
                const existing = getEncryptedSessionData<Record<string, any>>(posKey) || {};
                existing[id!] = {
                    menus: savedMenus,
                    timestamp: new Date().toISOString(),
                    type: "POS_ORDER",
                    loungeId: loungeId,
                    tokenNumber: res?.token_number || "",
                    isNew: false,
                };
                setEncryptedSessionData(posKey, existing);
                // POS ko notify karo
                const s = posSocket();
                const userInfoStr = localStorage.getItem("user_info");
                const userInfo = userInfoStr ? JSON.parse(userInfoStr) : null;
                const emitPayload = {
                    table_no: id,
                    lounge_id: String(loungeId || ""),
                    token_number: res?.token_number || "",
                    action: 'add_menu_items_on_table',
                    menus: menusPayload,
                    waiter_id: userInfo?.id || "",
                    captain_id: "",
                    persons: 1,
                    special_note: "",
                };
                if (s.connected) s.emit("waiterOrderCreated", emitPayload);
                else { s.once('connect', () => s.emit("waiterOrderCreated", emitPayload)); s.connect(); }

                removeQrOrderFromCache();
                setSelectedMenuItem([]);
                navigate(`/table-view`);
            })
            .catch(() => setLoading(false));
    };

    const removeQrOrderFromCache = () => {
        if (!id || !qrOrder) return;
        const cachedOrders = getEncryptedSessionData<Record<string, QrOrderPayload>>(qrOrderCacheKey) || {};
        delete cachedOrders[id];
        setEncryptedSessionData(qrOrderCacheKey, cachedOrders);
    };

    const onClose = () => {
        setSelectedMenuItem([]);
        navigate(`/table-view`);
    };

    return (
        <>
            <AddItemModal isShow={showModal} isClose={handleModalClose} data={{ loungeId: loungeId as any, tableNo: id as any }} />
            <div className="col-lg-12 bg-white p-1 rounded-1 font-12 w-100 h-100">
                {/* Header */}
                <div className="d-flex align-items-center justify-content-between category-ids">
                    <div className="d-flex align-items-center gap-2">
                        <label className="ms-2 font-12">Table No:</label>
                        <div className="font-12"><strong>{id || "-"}</strong></div>
                    </div>
                    <button className="btn btn-brand-1 px-3" onClick={() => setShowModal(true)}>Add Item</button>
                </div>

                {/* QR customer info */}
                {qrOrder && qrOrder.name && (
                    <div className="qr-order-customer-info d-flex justify-content-between">
                        <span><strong>Name:</strong> {qrOrder.name || "-"}</span>
                        <span><strong>Mobile:</strong> {qrOrder.mobile ? `+91 ${qrOrder.mobile}` : "-"}</span>
                    </div>
                )}
                <MobileLoader show={showFullPageLoader} />
                {/* Items table */}
                <div className="order-table-wrapper">
                    <table cellPadding="6" className="table table-bordered align-middle mt-2 order-table">
                        <thead className="custom-tbody">
                            <tr>
                                <th>Item</th>
                                <th>Qty.</th>
                                <th>Price</th>
                                <th>Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {selectedMenuItem && selectedMenuItem.length > 0 ? (
                                selectedMenuItem.map((item: MenuAttributesUpdated, index: number) => (
                                    <tr key={index.toString()}>
                                        <td className="w-25">
                                            <div className="d-flex align-items-center gap-1">
                                                <button
                                                    className="btn btn-danger delete-btn"
                                                    tabIndex={-1}
                                                    onClick={() => onRemoveMenuItem({ index, menu_id: item.menu_id as any, kot_id: item.kot_id, token_number: tokenNumber })}
                                                >
                                                    <TrashFill />
                                                </button>
                                                <span className="item-name fw-bold text-uppercase" style={{ overflow: "hidden" }}>
                                                    {item.name}
                                                </span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center gap-1">
                                                <button className="btn btn-danger qty-btn" onClick={() => onRemoveItemQuantity(item.id as any)}>-</button>
                                                <span className="fw-bold text-center px-2">{item.quantity}</span>
                                                <button className="btn btn-danger qty-btn" onClick={() => onAddItemQuantity(item.id as any)}>+</button>
                                            </div>
                                        </td>
                                        <td className="text-end">{Math.round(Number(item.gross_price) || 0).toFixed(2)}</td>
                                        <td className="text-end">{(Math.round(Number(item.gross_price) || 0) * Number(item.quantity)).toFixed(2)}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan={4} className="text-center text-muted">No items added</td></tr>
                            )}
                            <tr className="total-section">
                                <td colSpan={2} />
                                <td align="right"><strong className="font-12">Total Qty</strong></td>
                                <td className="text-end">{totalQuantity}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                {/* Footer */}
                <div className="offcanvas-footer mt-3 text-center">
                    <button className="btn btn-brand-1 me-3 px-3" type="submit" id="saveUserButton" onClick={() => onSaveAndPrintOrder()}>Save</button>
                    <button className="btn border-button" type="button" onClick={() => onClose()}>Cancel</button>
                </div>
            </div>
        </>
    );
};

export default OrderView;
